<template>
  <div class="bg-white min-h-screen flex flex-col justify-between">
    <!-- Navbar -->
    <nav class="bg-black text-white rounded-xl mx-2 sm:mx-4 mt-2 sm:mt-4 pr-2 sm:pr-4 md:pr-6 lg:pr-8 lg:pl-0">
      <div class="mx-auto flex justify-between items-center py-2 sm:py-4">
        <!-- Logo and Brand -->
        <div class="flex items-center space-x-2 active:text-[#34dc1c] sm:space-x-3">
          <div class="w-[68px] sm:w-[85px] h-15 sm:h-15">
            <img src="@/assets/logo.png" alt="Neuron Logo" class="h-full object-contain" />
          </div>
          <div class="font-bold capitalize">
            <strong class="text-2xl sm:text-3xl md:text-5xl lg:text-6xl block">Neuron</strong>
            <span class="text-sm sm:text-lg md:text-xl lg:text-xl font-normal block">
              The brain of your analytics workflow
            </span>
          </div>
        </div>

        <!-- Desktop Navigation Links -->
        <div class="hidden md:flex space-x-6 lg:space-x-8 text-xl lg:text-2xl items-center">
          <router-link
            to="/"
            active-class="text-[#157813]"
            exact-active-class="text-[#157813] border-b-2 border-[#157813]"
            class="text-white hover:text-[#157813] transition-all duration-200 flex items-center space-x-2 pb-1"
          >
            <LucideHome class="w-5 h-5" />
            <span>Home</span>
          </router-link>

          <router-link
            to="/advanced-search"
            active-class="text-[#157813]"
            exact-active-class="text-[#157813] border-b-2 border-[#157813]"
            class="text-white hover:text-[#157813] transition-all duration-200 flex items-center space-x-2 pb-1"
          >
            <LucideSearch class="w-5 h-5" />
            <span>Advanced Search</span>
          </router-link>

          <router-link
            to="/queries"
            active-class="text-[#157813]"
            exact-active-class="text-[#157813] border-b-2 border-[#157813]"
            class="text-white hover:text-[#157813] transition-all duration-200 flex items-center space-x-2 pb-1"
          >
            <LucideDatabase class="w-5 h-5" />
            <span>Feature Store</span>
          </router-link>

          <router-link
            to="/summary"
            active-class="text-[#157813]"
            exact-active-class="text-[#157813] border-b-2 border-[#157813]"
            class="text-white hover:text-[#157813] transition-all duration-200 flex items-center space-x-2 pb-1"
          >
            <LucideBarChart3 class="w-5 h-5" />
            <span>Summary</span>
          </router-link>

        </div>

        <!-- Mobile Menu Button -->
        <div class="md:hidden pr-2 sm:pr-3">
          <button @click="toggleMobileMenu" class="focus:outline-none p-2" aria-label="Toggle menu">
            <LucideMenu v-if="!isMobileMenuOpen" class="w-6 h-6" />
            <LucideX v-else class="w-6 h-6" />
          </button>
        </div>
      </div>

      <!-- Mobile Navigation Links -->
      <div
        v-if="isMobileMenuOpen"
        class="rounded-xl bg-black bg-opacity-95 text-white flex flex-col space-y-4 px-4 sm:px-10 w-[95%] py-4 md:hidden"
      >
        <router-link
          to="/"
          active-class="text-[#157813]"
          exact-active-class="text-[#157813] bg-[#157813]/5 rounded-lg"
          class="hover:text-[#157813] hover:bg-[#157813]/5 flex items-center space-x-2 p-2 transition-all duration-200 rounded-lg"
          @click="closeMobileMenu"
        >
          <LucideHome class="w-5 h-5" />
          <span>Home</span>
        </router-link>

        <router-link
          to="/advanced-search"
          active-class="text-[#157813]"
          exact-active-class="text-[#157813] bg-[#157813]/5 rounded-lg"
          class="hover:text-[#157813] hover:bg-[#157813]/5 flex items-center space-x-2 p-2 transition-all duration-200 rounded-lg"
          @click="closeMobileMenu"
        >
          <LucideSearch class="w-5 h-5" />
          <span>Advanced Search</span>
        </router-link>

        <router-link
          to="/queries"
          active-class="text-[#157813]"
          exact-active-class="text-[#157813] bg-[#157813]/5 rounded-lg"
          class="hover:text-[#157813] hover:bg-[#157813]/5 flex items-center space-x-2 p-2 transition-all duration-200 rounded-lg"
          @click="closeMobileMenu"
        >
          <LucideDatabase class="w-5 h-5" />
          <span>Feature Store</span>
        </router-link>

        <router-link
          to="/summary"
          active-class="text-[#157813]"
          exact-active-class="text-[#157813] bg-[#157813]/5 rounded-lg"
          class="hover:text-[#157813] hover:bg-[#157813]/5 flex items-center space-x-2 p-2 transition-all duration-200 rounded-lg"
          @click="closeMobileMenu"
        >
          <LucideBarChart3 class="w-5 h-5" />
          <span>Summary</span>
        </router-link>

      </div>
    </nav>

    <!-- Page Content -->
    <router-view />

    <!-- Footer -->
    <footer class="bg-black bg-opacity-95 text-white py-3 sm:py-4 text-center text-sm sm:text-base">
      <p>&copy; {{ currentYear }} The Neuron. all Rights Reserved</p>
    </footer>
  </div>
</template>

<script lang="ts">
import { LucideBarChart3, LucideDatabase, LucideHome, LucideMenu, LucideSearch, LucideX } from "lucide-vue-next";
import { defineComponent, ref } from "vue";
import { useRouter } from "vue-router";

export default defineComponent({
  components: {
    // Icons
    LucideMenu,
    LucideX,
    LucideHome,
    LucideSearch,
    LucideDatabase,
    LucideBarChart3,
  },
  setup() {
    const router = useRouter();
    const isMobileMenuOpen = ref(false);
    const currentYear = new Date().getFullYear();


    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
    };

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false;
    };

    return {
      // state
      isMobileMenuOpen,
      currentYear,
      // actions
      toggleMobileMenu,
      closeMobileMenu,
    };
  },
});
</script>

<style scoped>
/* keep any local styles if needed */
</style>
